/**
 * Cambridge HOTmaths CAPS Edition (www.hotmaths.co.za) homepage copy.
 *
 * Carried across from the Struts pages under `src/main/webapp/home/southafrica/` - `index`, `about`,
 * `requestAQuote` - in the same spirit as `hotmaths.js`: order and hierarchy change, words do not.
 *
 * The brand differs from HOTmaths in behaviour as well as words, and the shape below follows that:
 * - No private subscriptions and no card sales, so pricing is a quoted school rate with a "Request a quote"
 *   action rather than live product prices (`pricing.quote`).
 * - Registration and quotes stay on the existing Struts pages, so `links.register` and the nav point there.
 * - No sample lessons or video exist for this brand, so those sections are simply absent.
 * - The old About and Benefits pages are sections of the homepage now; "Request a quote", Terms and Privacy are
 *   React pages on the same bundle (see `routes`).
 */
const REQUEST_A_QUOTE_URL = '/home/southafrica/requestAQuote';

export const southAfricaContent = {
    productName: 'Cambridge HOTmaths CAPS Edition',

    links: {
        home: '/',
        register: '/home/southafrica/register',
    },

    intro: {
        heading: 'Cambridge HOTmaths',
        edition: 'CAPS Edition',
        standfirst: 'An interactive online maths learning, teaching and assessment resource for students '
            + 'and teachers, for individuals or whole classes, for school and at home.',
        primaryAction: {label: 'Get a 14-day trial', href: '#ch-home-trial'},
        secondaryAction: {label: 'Request a quote', href: REQUEST_A_QUOTE_URL},
    },

    features: {
        heading: 'What you get',
        items: [
            {label: 'Lessons', icon: 'lessons'},
            {label: 'Interactive and printable resources', icon: 'resources'},
            {label: 'Walkthroughs', icon: 'walkthrough'},
            {label: 'Questions', icon: 'questions'},
            // This brand's name for Scorcher (south_africa.json scorcherName).
            {label: 'Quickfire quiz', icon: 'scorcher'},
            {label: 'Dictionary', icon: 'dictionary'},
        ],
    },

    /** From index/index-body.jsp's "Free trial" box. */
    trial: {
        sectionHeading: 'Free trial',
        heading: 'Get a 14-day trial',
        body: 'Experience Cambridge HOTmaths CAPS Edition - sign up here for a free 14-day trial.',
    },

    /** From requestAQuote/index-body.jsp. The rate is copy there too; there is no product behind it. */
    pricing: {
        heading: 'Pricing',
        quote: true,
        schoolPrice: 'R70.00',
        // The legacy page says "around R70.00" - it is a guide, the quote is the price.
        schoolNote: 'approximately, per learner per year, inclusive of VAT',
        body: 'This product is presently not available to private subscribers. Schools can access Cambridge '
            + 'HOTmaths CAPS Edition by purchasing one-year renewable subscriptions per learner.',
        quoteNote: 'We can provide tailored quotations for schools, districts, provincial and national '
            + 'education departments, as well as for corporate social investment or NGO-based initiatives.',
        action: {label: 'Request a quote', href: REQUEST_A_QUOTE_URL},
    },

    /** From about/index-body.jsp. The system requirements stay on the About page. */
    about: {
        heading: 'About Cambridge HOTmaths CAPS Edition',
        paragraphs: [
            'Cambridge HOTmaths CAPS Edition is part of the HOTmaths comprehensive mathematics learning system '
                + '– an interactive online maths learning, teaching and assessment resource for students and '
                + 'teachers, for individuals or whole classes, for school and at home.',
            'It features flexible, enriching and support resources (printable and online) and mapping for your '
                + 'Senior Phase CAPS Mathematics classroom. Developed by a dedicated group of educators, '
                + 'technicians and designers, Cambridge HOTmaths CAPS Edition is a high quality maths resource '
                + 'for students and teachers.',
            'Cambridge University Press has been publishing high-quality curriculum-based materials for school '
                + 'students for over 150 years. The African Branch of Cambridge University Press publishes a wide '
                + 'range of print and digital publications in over 30 languages spoken on the continent.',
        ],
    },

    /** From benefits/index-body.jsp, which now redirects here (#ch-home-benefits). */
    benefits: {
        heading: 'Benefits',
        groups: [
            {
                heading: 'In the classroom',
                items: [
                    'Saves teacher time in preparing activities, checking homework, marking and recording progress',
                    'Contains curriculum-based teaching, learning and assessment resources',
                    'Makes maths meaningful and engaging with hundreds of visual demonstrations, explorations and '
                        + 'animations',
                    'Charts student progress with integrated diagnostic tests and other assessment activities that '
                        + 'can be tracked throughout the learning management system',
                    'Caters for all ability levels from underperforming to advanced, and allows students to work at '
                        + 'their own pace',
                    'Offers creative teaching ideas for integrating ICT in your classroom',
                    'Provides access 24 hours a day to web-based resources for maths to help with planning',
                ],
            },
            {
                heading: 'At home',
                items: [
                    'Improves your maths performance by reinforcing the topics you are learning at school with '
                        + 'unlimited access',
                    "Provides an effective, inexpensive and world's best home tutoring system that can be accessed "
                        + 'anywhere you have the internet',
                    'Makes maths meaningful and engaging with hundreds of visual demonstrations, explorations and '
                        + 'animations',
                    'Helps identify your maths weaknesses',
                    'Builds your confidence as you succeed at each topic',
                    'Encourages independent learning as you achieve at your own pace and level',
                    'Helps you catch up on work missed if you are absent from school',
                ],
            },
        ],
    },

    /** From requestAQuote/index-body.jsp and the form it included (forms/schoolsPricingForm.jsp). */
    quote: {
        heading: 'Request a quote',
        paragraphs: [
            'This product is presently not available to private subscribers. Schools can access Cambridge HOTmaths '
                + 'CAPS Edition by purchasing one-year renewable subscriptions per learner. The current subscription '
                + 'rate is around R70.00 per user per annum, inclusive of VAT.',
            'We can provide tailored quotations for schools, districts, provincial and national education '
                + 'departments, as well as for corporate social investment or NGO-based initiatives.',
            'To obtain a quotation or request further information, please complete the form.',
        ],
        // From the old Contact us page (now redirected here), so the general enquiry address is not lost.
        otherEnquiries: 'For other enquiries, email',
        otherEnquiriesEmail: 'cambridge@hotmaths.co.za',
    },

    /** From index/index-body.jsp's "Combine with Cambridge books" box, linking to the local site it names. */
    otherProducts: {
        heading: 'Combine with Cambridge books',
        body: 'Enhance your learning experience with additional CAPS-aligned resources for Mathematics.',
        link: {label: 'www.cup.co.za', href: 'https://www.cup.co.za'},
    },

    /**
     * Wording for the registration page. Behaviour - code lengths, terms and privacy pages - is in
     * south_africa.json and arrives through /web/ui/registration/public/config.
     */
    registration: {
        codeLabel: 'Access code',
        codeSectionTitle: 'Your access code',
    },

    /**
     * The brand's other public pages, rendered by the homepage bundle and matched with wouter (index.js). The
     * server serves these paths directly (WEB-INF/urlrewrite.xml); anything else falls through to the homepage.
     */
    routes: [
        {path: REQUEST_A_QUOTE_URL, page: 'quote'},
        {path: '/home/southafrica/terms', page: 'legal', title: 'Terms of Use',
            fragment: '/home/southafrica/fragments/terms.html'},
        {path: '/home/southafrica/privacy', page: 'legal', title: 'Privacy Policy',
            fragment: '/home/southafrica/fragments/privacy.html'},
    ],

    nav: [
        // Sections of the homepage, written as "/#..." so they also work from the quote page.
        {label: 'About', href: '/#ch-home-about'},
        {label: 'Benefits', href: '/#ch-home-benefits'},
        {label: 'Request a quote', href: REQUEST_A_QUOTE_URL},
        // The CAPS Edition help section (south_africa.json helpUrl), without the domain so it also works on
        // integration and locally.
        {label: 'Help', href: '/usersCommon/showHelp.action#/1/210/'},
    ],
};

export default southAfricaContent;
